import { useEffect } from "react";
import { emptyUiConfigOverlay, type UiConfigOverlay } from "@repo/ui-config";

import {
  getUiConfigLastSyncedSerialized,
  getUiConfigOverlay,
  getUiConfigOverlayOwner,
  quarantineUiConfigOverlayForPrincipal,
  setUiConfigLastSyncedSerialized,
  setUiConfigOverlay,
  setUiConfigOverlayOwner,
  subscribeToUiConfigOverlay,
} from "./overlay-store";

/**
 * Client ↔ server sync for the UI-config overlay document (Slice 1 C2).
 *
 * The server document is the source of truth; localStorage (inside the
 * overlay store) is only a boot cache. Strategy:
 *
 * - **Initial reconcile**: fetch the server document. If the server has
 *   none (revision 0) and the local overlay is customized AND provably
 *   belongs to this principal (owner stamp), push local up (first device
 *   wins). Otherwise adopt the server document — except edits made while
 *   the fetch was in flight, which are pushed on top of the server
 *   revision instead of being discarded.
 * - **Writes**: store changes are debounced and pushed with the last
 *   known revision (optimistic concurrency). On CONFLICT the controller
 *   refreshes the revision and retries once with the local document as of
 *   the retry (latest user intent wins); a second conflict adopts the
 *   server document.
 * - Failed pushes leave the dirty state in place; the next store change
 *   retries. No queue, no offline log — Tier-0 config is low-stakes and
 *   last-write-wins by design.
 *
 * The owner stamp exists for shared browsers: the boot cache is global to
 * the profile, so promoting it into a DIFFERENT signed-in user's empty
 * server document would leak one user's layout/lens state into another's
 * account. Promotion therefore requires `owner === principalKey`; the
 * stamp is written on adoption and on every successful push.
 */

export type UiConfigSyncClient = {
  get(): Promise<{ document: UiConfigOverlay; revision: number }>;
  set(input: {
    document: UiConfigOverlay;
    expectedRevision: number;
  }): Promise<{ revision: number }>;
  isRevisionConflict(error: unknown): boolean;
};

const DEFAULT_DEBOUNCE_MS = 800;

function serialize(document: UiConfigOverlay): string {
  return JSON.stringify(document);
}

function isCustomized(document: UiConfigOverlay): boolean {
  return serialize(document) !== serialize(emptyUiConfigOverlay());
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : {};
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" && value !== null && !Array.isArray(value)
  );
}

/**
 * When both sides hold an object for the same id, merge field-wise (local
 * wins per field) so fields only the remote knows about — e.g. written by
 * a newer client under the same lens id — survive the rebase, honoring
 * the loose schemas' forward-compatibility guarantee. Non-object payloads
 * take the local value wholesale. (Field deletion isn't representable
 * here; absent fields simply keep the remote value.)
 */
function mergeIdPayload(remoteValue: unknown, localValue: unknown): unknown {
  if (isPlainObject(remoteValue) && isPlainObject(localValue)) {
    return { ...remoteValue, ...localValue };
  }
  return localValue;
}

/**
 * Section-granular three-way rebase: apply the deltas the user made
 * locally (boot → current) onto the freshly fetched remote document, so a
 * quick lens toggle made during a slow boot fetch can't wipe remote-only
 * state (e.g. surface order saved from another device). Granularity is
 * per lens id / per surface id / per unknown top-level section — matching
 * how the config layer treats overlays as bags of semantic sections.
 */
function rebaseLocalEditsOntoRemote(
  boot: UiConfigOverlay,
  current: UiConfigOverlay,
  remote: UiConfigOverlay,
): UiConfigOverlay {
  const result: Record<string, unknown> = {
    ...asRecord(remote),
    schemaVersion: current.schemaVersion,
  };

  for (const sectionKey of ["lenses", "surfaces"] as const) {
    const bootSection = asRecord(asRecord(boot)[sectionKey]);
    const currentSection = asRecord(asRecord(current)[sectionKey]);
    const merged: Record<string, unknown> = {
      ...asRecord(asRecord(remote)[sectionKey]),
    };
    let changed = sectionKey in asRecord(remote);
    const ids = new Set([
      ...Object.keys(bootSection),
      ...Object.keys(currentSection),
    ]);
    for (const id of ids) {
      if (JSON.stringify(bootSection[id]) === JSON.stringify(currentSection[id])) {
        continue;
      }
      changed = true;
      if (currentSection[id] === undefined) {
        delete merged[id];
      } else {
        merged[id] = mergeIdPayload(merged[id], currentSection[id]);
      }
    }
    if (changed) {
      result[sectionKey] = merged;
    }
  }

  const knownKeys = new Set(["schemaVersion", "lenses", "surfaces"]);
  const otherKeys = new Set(
    [...Object.keys(asRecord(boot)), ...Object.keys(asRecord(current))].filter(
      (key) => !knownKeys.has(key),
    ),
  );
  for (const key of otherKeys) {
    const before = JSON.stringify(asRecord(boot)[key]);
    const after = JSON.stringify(asRecord(current)[key]);
    if (before === after) {
      continue;
    }
    if (asRecord(current)[key] === undefined) {
      delete result[key];
    } else {
      result[key] = mergeIdPayload(result[key], asRecord(current)[key]);
    }
  }

  return result as UiConfigOverlay;
}

export function createUiConfigServerSync(
  client: UiConfigSyncClient,
  options: { debounceMs?: number; principalKey: string },
): { start(): () => void } {
  const debounceMs = options.debounceMs ?? DEFAULT_DEBOUNCE_MS;
  const { principalKey } = options;

  let stopped = false;
  let revision = 0;
  let lastSyncedSerialized: string | null = null;
  let applyingRemote = false;
  let pushTimer: ReturnType<typeof setTimeout> | null = null;
  let pushing = false;
  let repushRequested = false;
  let reconcilePromise: Promise<boolean> | null = null;
  // Bound in start() (needs the boot snapshot); single-flight with retry
  // on failure.
  let ensureReconciled: () => Promise<boolean> = () => Promise.resolve(false);

  function adoptServerDocument(document: UiConfigOverlay, rev: number): void {
    revision = rev;
    lastSyncedSerialized = serialize(document);
    setUiConfigLastSyncedSerialized(lastSyncedSerialized);
    setUiConfigOverlayOwner(principalKey);
    applyingRemote = true;
    try {
      setUiConfigOverlay(document);
    } finally {
      applyingRemote = false;
    }
  }

  function recordSynced(serialized: string, rev: number): void {
    revision = rev;
    lastSyncedSerialized = serialized;
    setUiConfigLastSyncedSerialized(serialized);
    setUiConfigOverlayOwner(principalKey);
  }

  async function push(): Promise<void> {
    if (stopped || pushing) {
      repushRequested = pushing;
      return;
    }

    pushing = true;
    try {
      // No push ever runs against an unreconciled server state: with a
      // failed boot read we have no revision baseline and no proof the
      // local cache belongs to this principal, so pushing could insert a
      // stale/foreign cache into this user's document. Retry the
      // reconcile here instead; on failure stay local-only.
      if (!(await ensureReconciled()) || stopped) {
        return;
      }

      const document = getUiConfigOverlay();
      const serialized = serialize(document);
      if (serialized === lastSyncedSerialized) {
        return;
      }

      try {
        const result = await client.set({
          document,
          expectedRevision: revision,
        });
        // A stop during the await means another principal's controller may
        // already own the store — recording here would re-stamp the owner
        // for the OLD principal and defeat the quarantine.
        if (stopped) {
          return;
        }
        recordSynced(serialized, result.revision);
      } catch (error) {
        if (!client.isRevisionConflict(error)) {
          return; // Leave dirty; the next change retries.
        }
        // Someone else (another device) wrote first: refresh the revision
        // and retry once with the LOCAL DELTAS REBASED onto the refreshed
        // remote — pushing the raw local document would wipe remote-only
        // sections the other device just wrote (set replaces whole
        // documents). Capture the exact payload BEFORE the await so edits
        // landing mid-flight stay dirty and get their own push.
        const remote = await client.get();
        if (stopped) {
          return;
        }
        let retryBase = emptyUiConfigOverlay();
        if (lastSyncedSerialized !== null) {
          try {
            retryBase = JSON.parse(lastSyncedSerialized) as UiConfigOverlay;
          } catch {
            // Fall back to treating everything local as a delta.
          }
        }
        const localAtRetry = getUiConfigOverlay();
        const retryDocument = rebaseLocalEditsOntoRemote(
          retryBase,
          localAtRetry,
          remote.document,
        );
        const retrySerialized = serialize(retryDocument);
        try {
          const retry = await client.set({
            document: retryDocument,
            expectedRevision: remote.revision,
          });
          if (stopped) {
            return;
          }
          // Show the merged result locally (remote-only sections in). An
          // edit that landed while the retry was in flight is rebased ONTO
          // the merged result — otherwise its follow-up push would send
          // the pre-rebase document at the new revision and silently wipe
          // the remote-only sections the retry just preserved.
          const storeAfterRetry = getUiConfigOverlay();
          const nextLocal =
            storeAfterRetry === localAtRetry
              ? retryDocument
              : rebaseLocalEditsOntoRemote(
                  localAtRetry,
                  storeAfterRetry,
                  retryDocument,
                );
          applyingRemote = true;
          try {
            setUiConfigOverlay(nextLocal);
          } finally {
            applyingRemote = false;
          }
          recordSynced(retrySerialized, retry.revision);
          if (storeAfterRetry !== localAtRetry) {
            // The rebased mid-flight edit is dirty relative to the new
            // baseline; push it.
            schedulePush();
          }
        } catch (retryError) {
          if (client.isRevisionConflict(retryError) && !stopped) {
            // The server moved again between refresh and retry — the
            // pre-retry snapshot is stale, so adopt a FRESH read.
            const fresh = await client.get();
            if (!stopped) {
              adoptServerDocument(fresh.document, fresh.revision);
            }
          }
        }
      }
    } finally {
      pushing = false;
      // Only follow up when a NEW edit arrived mid-flight. Failed pushes
      // deliberately stay dirty until the next user edit — rescheduling on
      // dirtiness alone would hammer the API forever on persistent
      // non-conflict failures (oversized doc, 403, 5xx).
      if (repushRequested && !stopped) {
        repushRequested = false;
        schedulePush();
      }
    }
  }

  function schedulePush(): void {
    if (pushTimer !== null) {
      clearTimeout(pushTimer);
    }
    pushTimer = setTimeout(() => {
      pushTimer = null;
      void push();
    }, debounceMs);
  }

  function start(): () => void {
    // Another principal's stamped cache must stop RENDERING the moment we
    // know who is signed in — not only when the server read wins.
    quarantineUiConfigOverlayForPrincipal(principalKey);

    const unsubscribe = subscribeToUiConfigOverlay(() => {
      if (!applyingRemote) {
        schedulePush();
      }
    });

    const bootDocument = getUiConfigOverlay();
    const bootSerialized = serialize(bootDocument);
    // Edits made during the boot fetch are only pushable when the base
    // they were made on can't contain another principal's state: either
    // the cache is stamped as ours, or it started empty.
    const bootBaseIsSafeToPush =
      getUiConfigOverlayOwner() === principalKey || !isCustomized(bootDocument);

    async function reconcileOnce(): Promise<boolean> {
      try {
        const remote = await client.get();
        if (stopped) {
          return false;
        }

        const current = getUiConfigOverlay();
        const currentSerialized = serialize(current);
        const localBelongsToPrincipal =
          getUiConfigOverlayOwner() === principalKey;

        if (
          remote.revision === 0 &&
          isCustomized(current) &&
          localBelongsToPrincipal
        ) {
          // First device to sync for THIS principal: promote local state.
          // Persist the baseline first (same rule as the rebase branches):
          // if the promotion push fails and the tab reloads, the next boot
          // must take the dirty-cache path, not adopt over the edits.
          revision = 0;
          lastSyncedSerialized = serialize(remote.document);
          setUiConfigOverlayOwner(principalKey);
          setUiConfigLastSyncedSerialized(lastSyncedSerialized);
          schedulePush();
          return true;
        }

        if (
          bootBaseIsSafeToPush &&
          currentSerialized !== bootSerialized &&
          currentSerialized !== serialize(remote.document)
        ) {
          // The user edited while the boot fetch was in flight: rebase the
          // local deltas onto the remote document (a whole-document push
          // of the boot cache would wipe remote-only sections, e.g. saved
          // surface order from another device), show that merged state,
          // and push it on top of the server revision.
          const rebased = rebaseLocalEditsOntoRemote(
            bootDocument,
            current,
            remote.document,
          );
          revision = remote.revision;
          lastSyncedSerialized = serialize(remote.document);
          // Persist owner + baseline BEFORE the (possibly never-running)
          // push: if the tab closes or the push fails, the next boot must
          // see a stamped cache that differs from the baseline — the dirty
          // path — instead of an unsafe cache to adopt over.
          setUiConfigOverlayOwner(principalKey);
          setUiConfigLastSyncedSerialized(lastSyncedSerialized);
          applyingRemote = true;
          try {
            setUiConfigOverlay(rebased);
          } finally {
            applyingRemote = false;
          }
          schedulePush();
          return true;
        }

        // Post-reload dirty cache: the cache is stamped OURS and differs
        // from what we LAST SYNCED (persisted baseline) — a push failed
        // before a reload. Rebase those unsynced edits onto the remote
        // document instead of silently adopting over them. Without the
        // baseline this is indistinguishable from being merely behind
        // another device (which must adopt), so only the baseline path
        // treats it as dirty.
        const persistedBaseline = getUiConfigLastSyncedSerialized();
        if (
          localBelongsToPrincipal &&
          persistedBaseline !== null &&
          currentSerialized !== persistedBaseline &&
          currentSerialized !== serialize(remote.document)
        ) {
          let baselineDocument = bootDocument;
          try {
            baselineDocument = JSON.parse(persistedBaseline) as UiConfigOverlay;
          } catch {
            // Corrupt baseline: fall back to the boot snapshot.
          }
          const rebased = rebaseLocalEditsOntoRemote(
            baselineDocument,
            current,
            remote.document,
          );
          revision = remote.revision;
          lastSyncedSerialized = serialize(remote.document);
          // Persist owner + baseline BEFORE the (possibly never-running)
          // push: if the tab closes or the push fails, the next boot must
          // see a stamped cache that differs from the baseline — the dirty
          // path — instead of an unsafe cache to adopt over.
          setUiConfigOverlayOwner(principalKey);
          setUiConfigLastSyncedSerialized(lastSyncedSerialized);
          applyingRemote = true;
          try {
            setUiConfigOverlay(rebased);
          } finally {
            applyingRemote = false;
          }
          schedulePush();
          return true;
        }

        adoptServerDocument(remote.document, remote.revision);
        return true;
      } catch {
        // Boot fetch failed: stay on the local cache, local-only. Pushes
        // are blocked until a reconcile succeeds (no revision baseline, no
        // ownership proof); ensureReconciled retries on the next attempt.
        return false;
      }
    }

    ensureReconciled = () => {
      reconcilePromise ??= reconcileOnce().then((ok) => {
        if (!ok) {
          reconcilePromise = null;
        }
        return ok;
      });
      return reconcilePromise;
    };

    void ensureReconciled();

    return () => {
      stopped = true;
      unsubscribe();
      if (pushTimer !== null) {
        clearTimeout(pushTimer);
        pushTimer = null;
      }
    };
  }

  return { start };
}

/**
 * Mounts the sync controller while `enabled` (authed + the
 * `addon_platform_ui_config` flag on). `principalKey` is the signed-in
 * USER id (never a wallet address — the same user has different wallets
 * across VMs); it scopes the local boot cache's owner stamp and is never
 * sent to the server. Client identity changes restart the controller, so
 * memoize the client at the call site.
 */
export function useUiConfigServerSync(input: {
  enabled: boolean;
  client: UiConfigSyncClient | null;
  principalKey: string | null;
}): void {
  const { client, enabled, principalKey } = input;
  useEffect(() => {
    if (!enabled || client === null || principalKey === null) {
      return;
    }
    return createUiConfigServerSync(client, { principalKey }).start();
  }, [client, enabled, principalKey]);
}
